import { Doc } from '@dadohr/shared/domainTypes/document';
import { AdpWorkforceNowConfig } from '@dadohr/shared/domainTypes/integrations/adp';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import {
  useUnsafeTranslation
} from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import {
  removeAdpWorkforceNowConfig,
  useAdpWorkforceNowConfig
} from './service';

const NAME = 'ADP Workforce Now';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<AdpWorkforceNowConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <IntegrationData>
          <strong>{t('Client ID')}</strong>
          <div>{config.data.clientId || ''}</div>
          <strong>{t('Client Secret')}</strong>
          <div>{masked(config.data.clientId)}</div>
          <strong>{t('SSL Client Certificate')}</strong>
          <div>{masked(config.data.sslClientCertificate)}</div>
          <strong>{t('SSL Client Key')}</strong>
          <div>{masked(config.data.sslClientKey)}</div>
        </IntegrationData>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeAdpWorkforceNowConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

const NewSetupDialog = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<AdpWorkforceNowConfig>) => {
  const { t } = useUnsafeTranslation();
  const [state, setState] = useState<{
    clientId: string;
    clientSecret: string;
    sslClientCertificate: string;
    sslClientKey: string;
  }>({
    clientId: '',
    clientSecret: '',
    sslClientCertificate: '',
    sslClientKey: ''
  });

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('Set up ADP Workforce to read from and write to ADP.')}
        </Alert>

        <FormRow>
          <CustomInputLabel label={t('Client ID')} fullWidth>
            <TextField
              variant="outlined"
              value={state.clientId}
              onChange={(ev) =>
                setState((s) => ({ ...s, clientId: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
        <FormRow>
          <CustomInputLabel label={t('Client Secret')} fullWidth>
            <TextField
              variant="outlined"
              value={state.clientSecret}
              onChange={(ev) =>
                setState((s) => ({ ...s, clientSecret: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
        <FormRow>
          <CustomInputLabel label={t('SSL Client Certificate')} fullWidth>
            <TextField
              variant="outlined"
              value={state.sslClientCertificate}
              onChange={(ev) =>
                setState((s) => ({
                  ...s,
                  sslClientCertificate: ev.target.value
                }))
              }
              fullWidth
              multiline
              rows={6}
            />
          </CustomInputLabel>
        </FormRow>
        <FormRow>
          <CustomInputLabel label={t('SSL Client Key')} fullWidth>
            <TextField
              variant="outlined"
              value={state.sslClientKey}
              onChange={(ev) =>
                setState((s) => ({ ...s, sslClientKey: ev.target.value }))
              }
              fullWidth
              multiline
              rows={6}
            />
          </CustomInputLabel>
        </FormRow>
      </Content>
      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(
                  'integrations_adp-verifyCredentialsAndStore',
                  {
                    orgId: config.data.orgId,
                    creds: {
                      clientId: state.clientId,
                      clientSecret: state.clientSecret,
                      sslClientCertificate: state.sslClientCertificate,
                      sslClientKey: state.sslClientKey
                    }
                  }
                ).then(onSuccessfulIntegration)
              }
              disabled={
                !state.clientId ||
                !state.clientSecret ||
                !state.sslClientCertificate ||
                !state.sslClientKey
              }
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

const SetupDialogV2 = ({
  config,
  open,
  onClose,
  ...other
}: IntegrationSetupDialogWithConfigProperties<AdpWorkforceNowConfig>) => {
  const isSetup = !!(
    config?.data &&
    config?.data.clientId &&
    config?.data.clientSecret &&
    config?.data.sslClientCertificate &&
    config?.data.sslClientKey
  );

  if (!config) {
    return null;
  }

  if (isSetup) {
    return (
      <ExistingSetupDialog config={config} open={open} onClose={onClose} />
    );
  }

  return (
    <NewSetupDialog config={config} open={open} onClose={onClose} {...other} />
  );
};

export const AdpWorkforceNowSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useAdpWorkforceNowConfig(org.id, userId);
  const cfg = config || null;

  return (
    <>
      {
        <IntegrationConfigurationButton
          name={NAME}
          functionalities={functionalities}
          logoUrl="/images/integrations-logos/adp.svg"
          active={!!cfg?.data.active}
          interactive={interactive}
          renderSetupDialog={({
            open,
            onClose,
            onFailure,
            onSuccessfulIntegration
          }: IntegrationSetupDialogProperties) => (
            <SetupDialogV2
              config={cfg}
              open={open}
              onClose={onClose}
              onFailure={onFailure}
              onSuccessfulIntegration={onSuccessfulIntegration}
            />
          )}
        />
      }
    </>
  );
};
